import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Badge,
} from '@mui/material';
import {
  Psychology,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Error,
  Info,
  Lightbulb,
  Timeline,
  AttachMoney,
  Group,
  Schedule,
  ExpandMore,
  Refresh,
  AutoAwesome,
  SmartToy,
  Analytics,
} from '@mui/icons-material';

interface AIInsightsPanelProps {
  projectData?: any;
  loading?: boolean;
}

interface AIInsight {
  id: string;
  type: 'risk' | 'opportunity' | 'recommendation' | 'prediction' | 'anomaly';
  category: 'schedule' | 'cost' | 'resources' | 'quality' | 'safety';
  title: string;
  description: string;
  confidence: number;
  impact: 'low' | 'medium' | 'high' | 'critical';
  priority: number;
  actionable: boolean;
  timestamp: string;
  details?: string[];
  metrics?: { [key: string]: number | string };
}

const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({
  projectData,
  loading = false,
}) => {
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedInsight, setExpandedInsight] = useState<string | false>(false);
  useEffect(() => {
    // Load insights from provided projectData (live data)
    setInsights(Array.isArray(projectData) ? projectData : []);
  }, [projectData]);

  const runAIAnalysis = async () => {
    // Placeholder: trigger analysis flow without injecting mock data
    setIsAnalyzing(true);
    setTimeout(() => setIsAnalyzing(false), 1500);
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'risk': return <Warning color="error" />;
      case 'opportunity': return <TrendingUp color="success" />;
      case 'recommendation': return <Lightbulb color="primary" />;
      case 'prediction': return <Timeline color="info" />;
      case 'anomaly': return <Error color="error" />;
      default: return <Info />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'schedule': return <Schedule />;
      case 'cost': return <AttachMoney />;
      case 'resources': return <Group />;
      case 'quality': return <CheckCircle />;
      case 'safety': return <Warning />;
      default: return <Analytics />;
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const filteredInsights = selectedCategory === 'all' 
    ? insights 
    : insights.filter(insight => insight.category === selectedCategory);

  const sortedInsights = filteredInsights.sort((a, b) => a.priority - b.priority);

  const categories = ['all', 'schedule', 'cost', 'resources', 'quality', 'safety'];

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            AI Insights & Recommendations
          </Typography>
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Loading AI analysis...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <SmartToy fontSize="small" />
            </Avatar>
            <Typography variant="h6">
              AI Insights & Recommendations
            </Typography>
            <Badge badgeContent={insights.length} color="primary" />
          </Box>
          <Button
            variant="outlined"
            startIcon={isAnalyzing ? <AutoAwesome /> : <Refresh />}
            onClick={runAIAnalysis}
            disabled={isAnalyzing}
            size="small"
          >
            {isAnalyzing ? 'Analyzing...' : 'Run AI Analysis'}
          </Button>
        </Box>

        {isAnalyzing && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              AI is analyzing project data...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {/* Category Filter */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          {categories.map((category) => (
            <Chip
              key={category}
              label={category.charAt(0).toUpperCase() + category.slice(1)}
              onClick={() => setSelectedCategory(category)}
              color={selectedCategory === category ? 'primary' : 'default'}
              variant={selectedCategory === category ? 'filled' : 'outlined'}
              size="small"
              icon={category !== 'all' ? getCategoryIcon(category) : <Psychology />}
            />
          ))}
        </Box>

        {/* Insights List */}
        <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
          {sortedInsights.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
              <SmartToy sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                No insights available. Run AI analysis to generate recommendations.
              </Typography>
            </Paper>
          ) : (
            sortedInsights.map((insight, index) => (
              <Accordion
                key={insight.id}
                expanded={expandedInsight === insight.id}
                onChange={(_, isExpanded) => setExpandedInsight(isExpanded ? insight.id : false)}
                sx={{ mb: 1, '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getInsightIcon(insight.type)}
                      <Chip 
                        label={insight.type} 
                        size="small" 
                        color={getImpactColor(insight.impact) as any}
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2">
                        {insight.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {insight.description}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {insight.confidence}% confidence
                      </Typography>
                      <Chip 
                        label={insight.impact} 
                        size="small" 
                        color={getImpactColor(insight.impact) as any}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" paragraph>
                      {insight.description}
                    </Typography>
                    
                    {insight.details && (
                      <>
                        <Typography variant="subtitle2" gutterBottom>
                          📋 Details & Recommendations:
                        </Typography>
                        <List dense>
                          {insight.details.map((detail, idx) => (
                            <ListItem key={idx} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckCircle fontSize="small" color="primary" />
                              </ListItemIcon>
                              <ListItemText primary={detail} />
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}
                    
                    {insight.metrics && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          📊 Key Metrics:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {Object.entries(insight.metrics).map(([key, value]) => (
                            <Paper key={key} sx={{ p: 1, bgcolor: 'grey.50' }}>
                              <Typography variant="caption" color="text.secondary">
                                {key}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {value}
                              </Typography>
                            </Paper>
                          ))}
                        </Box>
                      </>
                    )}
                    
                    {insight.actionable && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          💡 This insight is actionable. Consider implementing the recommended actions to optimize project outcomes.
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AIInsightsPanel;